import { getCurrentSession, getCurrentUser } from "./cognito";
import { isAuthenticated } from "@/lib/auth";
import { saveCheckoutIntent } from "@/lib/cookies";

const API_BASE_URL =
	process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.taxlienvest.com";

export const STRIPE_PRO_PRICE_ID = "price_1PVcc1BOjx3ifW0i2G763bFK";

/**
 * Get the access token for API requests
 */
const getAccessToken = async (): Promise<string | null> => {
	try {
		const session = await getCurrentSession();
		const accessToken = session.getAccessToken();
		return accessToken.getJwtToken();
	} catch (error) {
		console.error("Failed to get access token:", error);
		return null;
	}
};

const handleApiFailure = async (
	response: Response,
	endpoint: string,
	fallbackMessage: string
) => {
	const errorData = await response.json().catch(() => ({}));
	const message = errorData.message || errorData.error || fallbackMessage;
	throw new Error(message);
};

export interface SubscriptionResponse {
	id: string;
	status:
		| "active"
		| "canceled"
		| "past_due"
		| "trialing"
		| "incomplete"
		| "incomplete_expired";
	productId?: string;
	product_id?: string;
	currentPeriodStart?: number;
	start_date?: string | number;
	currentPeriodEnd?: number;
	current_period_end?: string | number;
	cancelAtPeriodEnd?: boolean;
	cancel_at?: string | number;
	planName?: string;
	period?: string;
	remainingDays?: number;
}

export interface UserDetails {
	name: string;
	email: string;
}

/**
 * Get current user's active subscription
 */
export const getActiveSubscription =
	async (): Promise<SubscriptionResponse | null> => {
		const token = await getAccessToken();
		if (!token) {
			return null;
		}

		const endpoint = "/subscriptions/me";

		const response = await fetch(`${API_BASE_URL}${endpoint}`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});

		if (response.status === 404) {
			return null; // No subscription found
		}

		if (!response.ok) {
			await handleApiFailure(
				response,
				endpoint,
				"Failed to get subscription"
			);
		}

		return response.json();
	};

/**
 * Get current user details from Cognito
 */
export const getUserDetails = async (): Promise<UserDetails | null> => {
	try {
		// Force fresh fetch from Cognito
		const user = await getCurrentUser({ bypassCache: true });
		if (!user) {
			return null;
		}

		console.log("Cognito user object:", user);

		// Extract attributes from Cognito user
		// AWS Amplify returns user.attributes as an object
		// But some APIs return UserAttributes as an array: [{Name: "email", Value: "..."}]
		let email = "";
		let name = "";

		// Try direct attributes first (standard Amplify format)
		if (user.attributes) {
			console.log("Found user.attributes:", user.attributes);
			email = user.attributes.email || "";
			name = user.attributes.name || "";
		}

		// Fallback to UserAttributes array format if direct attributes not available
		if (
			(!email || !name) &&
			user.UserAttributes &&
			Array.isArray(user.UserAttributes)
		) {
			console.log(
				"Found user.UserAttributes array:",
				user.UserAttributes
			);
			const emailAttr = user.UserAttributes.find(
				(attr: { Name: string; Value: string }) => attr.Name === "email"
			);
			if (emailAttr?.Value) {
				email = emailAttr.Value;
			}

			const nameAttr = user.UserAttributes.find(
				(attr: { Name: string; Value: string }) => attr.Name === "name"
			);
			if (nameAttr?.Value) {
				name = nameAttr.Value;
			}
		}

		console.log("Extracted name:", name, "email:", email);

		// Don't use email username as fallback, let the UI show "N/A" if name is not available
		// This is clearer for the user

		return {
			name: name || "",
			email: email || "",
		};
	} catch (error) {
		console.error("Failed to get user details from Cognito:", error);
		return null;
	}
};

/**
 * Cancel subscription
 */
export const cancelSubscription = async (
	reason: string,
	details?: string
): Promise<void> => {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const endpoint = "/subscriptions/cancel";

	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			reason,
			details,
		}),
	});

	if (!response.ok) {
		await handleApiFailure(
			response,
			endpoint,
			"Failed to cancel subscription"
		);
	}
};

/**
 * Create a Stripe Checkout Session for subscription
 */
export const createCheckoutSession = async (
	priceId: string,
	successUrl: string = `${window.location.origin}/dashboard`,
	cancelUrl: string = `${window.location.origin}/dashboard`
): Promise<{ url: string }> => {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const endpoint = "/subscriptions/create-checkout-session";

	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			priceId,
			successUrl,
			cancelUrl,
		}),
	});

	if (!response.ok) {
		await handleApiFailure(
			response,
			endpoint,
			"Failed to create checkout session"
		);
	}

	return response.json();
};

/**
 * Create a Stripe Checkout Session with authentication check
 * If user is not authenticated, redirects to login page with checkout intent
 * After login, user will be redirected to checkout automatically
 */
export const createCheckoutSessionWithAuth = async (
	priceId: string,
	successUrl?: string,
	cancelUrl?: string
): Promise<{ url: string }> => {
	// Check if user is authenticated
	const authenticated = await isAuthenticated();

	if (!authenticated) {
		// Store checkout intent in cookies for after login
		// Using cookies instead of sessionStorage to persist across OAuth redirects
		const checkoutIntent = {
			priceId,
			successUrl: successUrl || `${window.location.origin}/dashboard`,
			cancelUrl: cancelUrl || `${window.location.origin}/dashboard`,
			returnUrl: window.location.href, // Store current page URL to return after login
		};
		saveCheckoutIntent(checkoutIntent);

		// Redirect to login page with checkout action
		// Using href instead of replace() to preserve history so user can go back
		if (typeof window !== "undefined") {
			window.location.href = "/login?action=checkout";
		}

		// Throw error to stop execution
		throw new Error("Redirecting to login");
	}

	// User is authenticated, proceed with checkout
	return createCheckoutSession(priceId, successUrl, cancelUrl);
};

/**
 * Calculate remaining days until subscription period ends
 */
/**
 * Calculate remaining days until subscription period ends
 * Expects a timestamp (seconds/ms) or ISO string
 */
export const calculateRemainingDays = (
	currentPeriodEnd: number | string
): number => {
	const now = Date.now();
	// Handle both number (seconds) and ISO string
	// If the number is small (e.g. < 10000), it's likely a day count, not a timestamp
	if (typeof currentPeriodEnd === "number" && currentPeriodEnd < 1000000) {
		return currentPeriodEnd;
	}

	const endTime =
		typeof currentPeriodEnd === "number"
			? currentPeriodEnd * 1000
			: new Date(currentPeriodEnd).getTime();

	if (isNaN(endTime)) return 0;

	const diff = endTime - now;
	const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
	return Math.max(0, days);
};

/**
 * Format subscription period
 */
export const formatSubscriptionPeriod = (
	currentPeriodStart: number | string,
	currentPeriodEnd: number | string
): string => {
	const start =
		typeof currentPeriodStart === "number"
			? new Date(currentPeriodStart * 1000)
			: new Date(currentPeriodStart);
	const end =
		typeof currentPeriodEnd === "number"
			? new Date(currentPeriodEnd * 1000)
			: new Date(currentPeriodEnd);

	if (isNaN(start.getTime()) || isNaN(end.getTime())) {
		return "N/A";
	}

	const startMonth = start.toLocaleString("default", { month: "short" });
	const endMonth = end.toLocaleString("default", { month: "short" });

	if (startMonth === endMonth) {
		return `${startMonth} ${start.getDate()} - ${end.getDate()}`;
	}
	return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}`;
};
